import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import {
  alignVariants,
  type HeadingElement,
  toneVariants,
  truncateVariants,
} from "./shared";

const titleVariants = cva("", {
  variants: {
    size: {
      xl: [
        "font-(family-name:--font-family-title)",
        "text-(length:--font-size-title-xl)",
        "font-medium",
        "tracking-normal",
      ],
      lg: [
        "font-(family-name:--font-family-title)",
        "text-(length:--font-size-title-lg)",
        "font-medium",
        "tracking-[0.15px]",
      ],
      md: [
        "font-(family-name:--font-family-title)",
        "text-(length:--font-size-title-md)",
        "font-medium",
        "tracking-[0.15px]",
      ],
      sm: [
        "font-(family-name:--font-family-title)",
        "text-(length:--font-size-title-sm)",
        "font-medium",
        "tracking-[0.1px]",
      ],
      xs: [
        "font-(family-name:--font-family-title)",
        "text-(length:--font-size-title-xs)",
        "leading-(--line-height-16)",
        "font-medium",
        "tracking-[0.1px]",
      ],
    },
    tone: toneVariants,
    align: alignVariants,
    truncate: truncateVariants,
  },
  defaultVariants: {
    size: "md",
    tone: "default",
  },
});

type TitleSize = NonNullable<VariantProps<typeof titleVariants>["size"]>;
type TitleElement = HeadingElement | "span";

const defaultElementMap: Record<TitleSize, TitleElement> = {
  xl: "h4",
  lg: "h5",
  md: "h5",
  sm: "h6",
  xs: "h6",
};

export interface TitleProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "className">,
    VariantProps<typeof titleVariants> {
  /** Override the default HTML element */
  as?: TitleElement;
}

/**
 * Title component for card headers and subsection titles.
 * Uses Inter font family with medium weight.
 *
 * Sizes range from 22px (xl) down to 12px (xs).
 */
function Title({
  size = "md",
  tone,
  align,
  truncate,
  as,
  ...props
}: TitleProps) {
  const defaultElement = size ? defaultElementMap[size] : "h5";
  const Element = as || defaultElement;

  return React.createElement(Element, {
    className: titleVariants({ size, tone, align, truncate }),
    ...props,
  });
}

export { Title };
